/**
 * ConversationItem Component
 *
 * Individual conversation row in the conversation list.
 * Shows participant avatar, name, last message preview, timestamp,
 * unread badge, and online indicator.
 * Supports both direct messages and group conversations.
 */

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Archive, VolumeX, Users } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Conversation } from '@/types/messaging';

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a conversation (group or direct)
 */
const getDisplayName = (conversation: Conversation): string => {
  // Group conversation - use group name
  if (conversation.isGroup) {
    return conversation.groupName || 'Unnamed Group';
  }
  // Direct conversation - use participant name
  const { participant } = conversation;
  if (!participant) return 'Unknown';
  if (participant.firstName && participant.lastName) {
    return `${participant.firstName} ${participant.lastName}`;
  }
  if (participant.firstName) {
    return participant.firstName;
  }
  return participant.username;
};

/**
 * Truncate message preview
 */
const truncateMessage = (content?: string, maxLength: number = 45): string => {
  if (!content) return 'No messages yet';
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength).trim() + '...';
};

/**
 * Format relative time
 */
const formatTime = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: false });
  } catch {
    return '';
  }
};

export const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  onClick,
}) => {
  const displayName = getDisplayName(conversation);
  const isGroup = conversation.isGroup;

  // Get initials based on conversation type
  const initials = isGroup
    ? null // Groups use icon instead of initials
    : getInitials(
        conversation.participant?.firstName,
        conversation.participant?.lastName,
        conversation.participant?.username
      );

  // Get avatar based on conversation type
  const avatarSrc = isGroup ? conversation.groupAvatar : conversation.participant?.avatar;

  const hasUnread = conversation.unreadCount > 0;
  const isArchived = !!conversation.archivedAt;
  const isMuted = conversation.mutedUntil && new Date(conversation.mutedUntil) > new Date();

  // Get online status (only for direct conversations)
  const isOnline = !isGroup && conversation.participant?.isOnline;

  // Get member count for groups
  const memberCount = isGroup ? conversation.participants?.length || 0 : 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-150',
        'hover:bg-muted/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive && 'bg-muted',
        hasUnread && !isActive && 'bg-muted/30'
      )}
      aria-label={`${isGroup ? 'Group' : 'Conversation with'} ${displayName}${hasUnread ? `, ${conversation.unreadCount} unread messages` : ''}`}
      aria-current={isActive ? 'true' : undefined}
    >
      {/* Avatar with online indicator */}
      <div className="relative shrink-0">
        <Avatar className="h-12 w-12">
          {avatarSrc && <AvatarImage src={avatarSrc} alt={displayName} />}
          <AvatarFallback
            style={{
              backgroundColor: 'hsl(var(--primary) / 0.1)',
              color: 'hsl(var(--primary))',
            }}
            className="text-sm font-medium"
          >
            {isGroup ? <Users className="h-5 w-5" /> : initials}
          </AvatarFallback>
        </Avatar>

        {/* Online indicator (direct conversations only) */}
        {isOnline && (
          <span
            className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background"
            style={{ backgroundColor: 'hsl(var(--primary))' }}
            aria-label="Online"
          />
        )}

        {/* Group indicator badge */}
        {isGroup && (
          <span
            className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-background flex items-center justify-center"
            style={{ backgroundColor: 'hsl(var(--primary))' }}
          >
            <Users className="h-2.5 w-2.5 text-white" />
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        {/* Top row: name and time */}
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className={cn(
                'text-sm truncate',
                hasUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground'
              )}
            >
              {displayName}
            </span>

            {/* Group member count */}
            {isGroup && memberCount > 0 && (
              <span className="text-xs text-muted-foreground shrink-0">
                · {memberCount}
              </span>
            )}

            {/* Status icons */}
            {isArchived && (
              <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            )}
            {isMuted && (
              <VolumeX className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            )}
          </div>

          {/* Time */}
          {conversation.lastMessageAt && (
            <span
              className={cn(
                'text-xs shrink-0',
                hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'
              )}
            >
              {formatTime(conversation.lastMessageAt)}
            </span>
          )}
        </div>

        {/* Bottom row: message preview and unread badge */}
        <div className="flex items-center justify-between gap-2">
          <p
            className={cn(
              'text-sm truncate',
              hasUnread ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {truncateMessage(conversation.lastMessage?.content)}
          </p>

          {/* Unread badge */}
          {hasUnread && (
            <Badge
              className="h-5 min-w-[20px] px-1.5 text-xs shrink-0"
              style={{
                backgroundColor: 'hsl(var(--primary))',
                color: 'hsl(var(--primary-foreground))',
              }}
            >
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
};

export default ConversationItem;
